/**
 * ProfileSnapshotCard — quick read-only summary of the user's profile.
 * Update-profile button routes to `/app/members/my-profile`.
 */

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Avatar,
  Button,
  Card,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@/components/ui';
import { IconUserCircle } from '@tabler/icons-react';
import { useStaffPortalProfileService } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { formatDate, initials } from '../../utils/hrHelpers';

export function ProfileSnapshotCard() {
  const navigate = useNavigate();
  const service = useStaffPortalProfileService();
  // HR profile lives in a separate service DB and rarely carries an avatar —
  // fall back to the authenticated user (UserManagement), the same source
  // the main /profile page resolves avatars from.
  const { user: authUser } = useEnrichedAuth();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['hr', 'staff', 'dashboard', 'profile'],
    queryFn: () => service.getProfile(),
  });
  const p = profile;

  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <ThemeIcon size="md" variant="light" color="violet" radius="xl">
            <IconUserCircle size={16} />
          </ThemeIcon>
          <Title order={5}>Profile Snapshot</Title>
        </Group>
      </Group>

      {isLoading ? (
        <Skeleton height={70} radius="md" />
      ) : !p ? (
        <Text size="sm" c="dimmed">Profile unavailable.</Text>
      ) : (
        <Stack gap="md">
          <Group gap="sm" wrap="nowrap">
            <Avatar
              src={p.avatar || authUser?.avatar || undefined}
              size="lg"
              radius="xl"
              color="violet"
            >
              {initials(p.fullName ?? authUser?.displayName ?? `${p.name ?? ''} ${p.surname ?? ''}`)}
            </Avatar>
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Text size="sm" fw={600} truncate="end">
                {p.fullName ?? authUser?.displayName ?? (`${p.name ?? ''} ${p.surname ?? ''}`.trim() || '—')}
              </Text>
              {p.jobTitle && (
                <Text size="xs" c="dimmed" truncate="end">{p.jobTitle}</Text>
              )}
            </Stack>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Field label="Employee No" value={p.employeeNo} />
            <Field label="Job Title" value={p.jobTitle} />
            <Field label="Type" value={p.typeName} />
            <Field label="Status" value={p.statusName} />
            <Field label="Start Date" value={p.startDate ? formatDate(p.startDate) : '—'} />
            <Field
              label="End Date"
              value={p.endDate ? formatDate(p.endDate) : 'Ongoing'}
            />
          </SimpleGrid>
          <Button
            size="xs"
            variant="light"
            fullWidth
            onClick={() => navigate('/app/members/my-profile')}
          >
            Update profile
          </Button>
        </Stack>
      )}
    </Card>
  );
}

function Field({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" tt="uppercase">{label}</Text>
      <Text size="sm" fw={500} truncate="end">{value || '—'}</Text>
    </Stack>
  );
}
